import { z } from 'zod';
/**
 * Bình luận trong `.chilldoc` — APM-CMT-a, docs/02-sprint-plan.md "Bình luận trong tài liệu".
 *
 * Người dùng chốt 2026-09-29: bình luận **nằm trong tệp** (vỏ JSON), không có bảng
 * riêng. Vùng được bình luận mang mark `comment` (`attrs.id`); đối tượng khối (khu
 * vực vẽ, biểu đồ, công thức khối, khối code) mang thuộc tính `commentIds`. Luồng
 * trả lời, "đã xử lý", sửa / xoá sống ở đây.
 *
 * Đọc **lỏng từng mục**: một bình luận hỏng (tệp sửa tay) rơi một mình, không kéo
 * cả danh sách đi theo.
 */
export const CHILLDOC_MAX_COMMENTS = 500;
export const CHILLDOC_MAX_REPLIES = 100;
/** Nội dung một bình luận (markdown của ô soạn rich text). */
export const CHILLDOC_COMMENT_MAX_CHARS = 20_000;
/** Thuộc tính của node khối mang id bình luận — mark không gắn được lên node khối. */
export const COMMENT_IDS_ATTR = 'commentIds';
const entryFields = {
    id: z.string().trim().min(1).max(64),
    authorId: z.string().max(64),
    authorName: z.string().max(120),
    createdAt: z.iso.datetime(),
    /** Có ⇒ đã sửa sau khi đăng. */
    editedAt: z.iso.datetime().optional().catch(undefined),
    body: z.string().max(CHILLDOC_COMMENT_MAX_CHARS),
};
export const ChilldocCommentEntry = z.object(entryFields);
/** Giữ mục đọc được, bỏ mục hỏng, cắt ở `max`. */
function lenientList(item, max) {
    return z
        .array(z.unknown())
        .catch([])
        .transform((items) => items.flatMap((raw) => {
        const parsed = item.safeParse(raw);
        return parsed.success ? [parsed.data] : [];
    }).slice(0, max));
}
export const ChilldocCommentResolution = z.object({
    byId: z.string().max(64),
    byName: z.string().max(120),
    at: z.iso.datetime(),
});
export const ChilldocCommentThread = z.object({
    ...entryFields,
    replies: lenientList(ChilldocCommentEntry, CHILLDOC_MAX_REPLIES).default([]),
    /** Có ⇒ đã xử lý: vùng thôi tô nền, bong bóng ẩn (trừ khi bật "Hiện đã xử lý"). */
    resolved: ChilldocCommentResolution.optional().catch(undefined),
});
export const ChilldocComments = lenientList(ChilldocCommentThread, CHILLDOC_MAX_COMMENTS);
/** Id bình luận của một node JSON: mark `comment` và thuộc tính `commentIds` của node khối. */
function idsOfNode(node) {
    const ids = [];
    for (const mark of node.marks ?? []) {
        const id = mark.type === 'comment' ? mark.attrs?.['id'] : null;
        if (typeof id === 'string' && id)
            ids.push(id);
    }
    const own = node.attrs?.[COMMENT_IDS_ATTR];
    if (Array.isArray(own))
        for (const id of own)
            if (typeof id === 'string' && id)
                ids.push(id);
    return ids;
}
/** Mọi id bình luận còn neo trong `doc` (ProseMirror JSON). */
export function commentAnchorIds(doc) {
    const ids = new Set();
    const walk = (node) => {
        for (const id of idsOfNode(node))
            ids.add(id);
        for (const child of node.content ?? [])
            walk(child);
    };
    if (doc && typeof doc === 'object')
        walk(doc);
    return ids;
}
/** Bỏ mọi dấu neo bình luận khỏi `doc` — bản chụp chia sẻ không mang bình luận (APM-CMT-c). */
export function stripCommentAnchors(doc) {
    const walk = (node) => {
        const { [COMMENT_IDS_ATTR]: _ids, ...attrs } = node.attrs ?? {};
        const next = { ...node };
        if (node.attrs)
            Object.assign(next, { attrs });
        if (node.marks)
            Object.assign(next, { marks: node.marks.filter((m) => m.type !== 'comment') });
        if (node.content)
            Object.assign(next, { content: node.content.map(walk) });
        return next;
    };
    return (doc && typeof doc === 'object' ? walk(doc) : doc);
}
//# sourceMappingURL=chilldoc-comments.js.map